import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authLogin, authRegister, authGoogle, authMe, authLogout, authUpdateProfile, getToken } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const u = await authMe();
      setUser(u);
      return u;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: si on revient d'un callback OAuth (session_id dans le hash),
    // laisser AuthCallback échanger le session_id avant de vérifier /me.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Pas de token local -> inutile d'appeler /me (évite un 401 bruyant)
    if (!getToken()) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = async (email, password) => {
    const u = await authLogin(email, password);
    setUser(u);
    return u;
  };
  const register = async (payload) => {
    const u = await authRegister(payload);
    setUser(u);
    return u;
  };
  const loginWithGoogleSession = async (session_id) => {
    const checkout = localStorage.getItem("td_pending_checkout") || undefined;
    const u = await authGoogle(session_id, checkout);
    localStorage.removeItem("td_pending_checkout");
    setUser(u);
    return u;
  };
  const startGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const logout = async () => {
    await authLogout();
    setUser(null);
  };

  const updateProfile = async (payload) => {
    const u = await authUpdateProfile(payload);
    setUser(u);
    return u;
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin",
    isSubscriber: !!user?.is_subscriber || user?.role === "admin",
    needsProfile: !!user && !user.profile_completed,
    login,
    register,
    loginWithGoogleSession,
    startGoogleLogin,
    logout,
    updateProfile,
    refresh,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
